// ---------------------------------------------------------------------------
// Social proof content: customer segments, testimonials and outcome metrics.
//
// IMPORTANT FOR THE TEAM
// Everything in this file is placeholder copy written to show the intended shape
// and tone of real social proof. Replace each entry with a customer approved
// quote, name and logo before this is treated as a published claim. Keep the
// `approved` flag false until the customer has signed off in writing, and keep
// attributions anonymized (segment and role only) unless the customer has agreed
// to be named. Nothing renders unless `approved` is true.
// ---------------------------------------------------------------------------

/** Sentinel used in `Testimonial.name` when the customer will not be named. */
export const UNNAMED = "Anonymized";

export interface Testimonial {
  /** The pull quote. Keep it under about 40 words so it reads well in a card. */
  quote: string;
  /** Person's name, or `UNNAMED` when the customer will not be named. */
  name: string;
  /** Job title of the person quoted. */
  role: string;
  /** Company name, or an anonymized descriptor such as "Top 25 US card issuer". */
  company: string;
  /** Short result line shown under the quote. Keep it specific and measurable. */
  result: string;
  /** Set to true only after the customer has approved the quote in writing. */
  approved: boolean;
}

export interface CustomerSegment {
  /** Anonymized descriptor used in place of a logo until logos are cleared. */
  label: string;
  /** One line describing what that customer uses VerifyDeceased for. */
  useCase: string;
}

export interface ProofStat {
  value: string;
  label: string;
  detail: string;
}

export interface TrustSignal {
  icon: string;
  label: string;
  detail: string;
}

/**
 * Trust signals shown as a strip near the top of the home page.
 *
 * Every entry here must be a fact we can evidence on request. Do not add a
 * third party seal or badge to this list until the certificate is actually held
 * and the verification link works.
 */
export const TRUST_SIGNALS: TrustSignal[] = [
  {
    icon: "lock",
    label: "AES-256 and TLS 1.2+",
    detail: "Encrypted at rest and in transit on every connection"
  },
  {
    icon: "shield",
    label: "GLBA Safeguards aligned",
    detail: "Full 16 CFR Part 314 control mapping published"
  },
  {
    icon: "server",
    label: "US data residency",
    detail: "Hosting, processing and backups stay in US regions"
  },
  {
    icon: "trash",
    label: "Your records deleted",
    detail: "Never sold, never relicensed, deleted on schedule"
  },
  {
    icon: "card",
    label: "No match, no charge",
    detail: "You are only billed for confirmed deceased records"
  },
  {
    icon: "check",
    label: "No contract to start",
    detail: "No minimum, no subscription, no cancellation fee"
  }
];

/**
 * Anonymized customer descriptors shown in the trust strip.
 * Swap each entry for a real logo (SVG in /public) once marketing consent is on file.
 */
export const CUSTOMER_SEGMENTS: CustomerSegment[] = [
  { label: "National card issuer", useCase: "Applicant screening at origination" },
  { label: "Regional bank", useCase: "Deposit account and portfolio suppression" },
  { label: "Consumer lender", useCase: "Loan origination and servicing checks" },
  { label: "Life insurance carrier", useCase: "Claims and in force policy audits" },
  { label: "Accounts receivable agency", useCase: "Deceased suppression before outreach" },
  { label: "Background screening provider", useCase: "Identity checks during hiring" }
];

/**
 * Real customer quotes go here. The site renders nothing until an entry has
 * `approved: true`, so it is safe to stage quotes while consent is being chased.
 */
export const TESTIMONIALS: Testimonial[] = [];

/** Outcome metrics shown on the home page. Replace with measured results as they land. */
export const PROOF_STATS: ProofStat[] = [
  {
    value: "Within 7 days",
    label: "Typical record availability",
    detail: "Confirmed deceased records are usually live within a week of passing."
  },
  {
    value: "50 states + DC",
    label: "Nationwide coverage",
    detail: "Every state, the District of Columbia and nationwide address enrichment."
  },
  {
    value: "$0 for no match",
    label: "You only pay for matches",
    detail: "Records we cannot confirm are never billed, so testing costs nothing."
  }
];

/** Only approved testimonials are safe to render publicly. */
export const APPROVED_TESTIMONIALS: Testimonial[] = TESTIMONIALS.filter(item => item.approved);
